/**
 * Offer card component - Simplified display
 */
import { Offer } from '@/types';

interface OfferCardProps {
  offer: Offer;
  compact?: boolean;
}

export function OfferCard({ offer, compact = false }: OfferCardProps) {
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const isExpired = () => {
    if (!offer.endDate) return false;
    const endDateAny = offer.endDate as any;
    const endDate = endDateAny.toDate ? endDateAny.toDate() : new Date(endDateAny);
    return endDate < new Date();
  };

  if (compact) {
    return (
      <div className="relative overflow-hidden rounded-xl p-4 text-white">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>

        <div className="relative flex items-center gap-4">
          {/* Image/Icon */}
          <div className="flex-shrink-0">
            {offer.imageURL ? (
              <img
                src={offer.imageURL}
                alt={offer.title}
                className="w-16 h-16 object-contain rounded-lg bg-white/20 p-1"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-white/20 flex items-center justify-center">
                <span className="text-3xl">🎉</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg truncate">{offer.title}</h3>
            <p className="text-sm text-white/90 line-clamp-1">{offer.description}</p>
          </div>

          {/* Discount Badge */}
          <div className="flex-shrink-0 px-3 py-1 bg-white/20 rounded-full font-bold backdrop-blur-sm">
            {offer.discountText}
          </div>
        </div>

        <div className="relative mt-3 flex items-center justify-between text-xs text-white/80">
          <span>Válida hasta: {formatDate(offer.endDate)}</span>
          {isExpired() && (
            <span className="px-2 py-0.5 bg-danger/100/50 rounded text-white">Expirada</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-2xl text-white shadow-xl ${isExpired() ? 'opacity-60' : ''}`}>
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
      </div>

      {/* Status badge */}
      {isExpired() && (
        <div className="absolute top-4 right-4 px-3 py-1 bg-danger/100 rounded-full text-sm font-semibold z-10">
          Expirada
        </div>
      )}
      {!offer.isActive && !isExpired() && (
        <div className="absolute top-4 right-4 px-3 py-1 bg-surface-20 rounded-full text-sm font-semibold z-10">
          Inactiva
        </div>
      )}

      <div className="relative p-6">
        <div className="flex items-center gap-6">
          {/* Image/Icon area */}
          <div className="flex-shrink-0">
            {offer.imageURL ? (
              <img
                src={offer.imageURL}
                alt={offer.title}
                className="w-24 h-24 object-contain rounded-xl bg-white/20 p-2"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-white/20 flex items-center justify-center">
                <span className="text-5xl">🎉</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-2xl font-bold mb-1 truncate">
              {offer.title}
            </h3>
            <p className="text-white/90 text-sm mb-3 line-clamp-2">
              {offer.description}
            </p>
            <div className="inline-block px-4 py-2 bg-white/20 rounded-full font-bold text-lg backdrop-blur-sm">
              {offer.discountText}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-white/20 flex items-center justify-between text-sm">
          <div className="space-y-1">
            <div className="text-white/70">
              Desde: <span className="text-white font-medium">{formatDate(offer.startDate)}</span>
            </div>
            <div className="text-white/70">
              Hasta: <span className="text-white font-medium">{formatDate(offer.endDate)}</span>
            </div>
          </div>

          {offer.notificationsSent && (
            <div className="flex items-center gap-1 text-white/70">
              <span className="text-success">✓</span>
              Notificaciones enviadas
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
